<template>
  <section
    class="appointment-report ar-card ar-appointment-card"
    :class="{ 'ar-appointment-card--with-meeting': hasMeetingRow }"
    :aria-label="A11Y_COPY.appointmentData"
    :aria-busy="loading ? 'true' : null"
  >
    <dl class="ar-appointment-card__grid">
      <div
        v-for="field in fields"
        :key="field.key"
        class="ar-appointment-card__field"
      >
        <dt class="ar-appointment-card__label">
          {{ field.label }}
        </dt>
        <dd class="ar-appointment-card__value">
          <template v-if="loading">
            <span
              class="ar-appointment-card__skeleton"
              aria-hidden="true"
            />
          </template>
          <template v-else>
            <span
              v-if="field.kind === 'person'"
              class="ar-avatar"
              aria-hidden="true"
            >{{ field.initials }}</span>
            <span
              v-else
              class="ar-appointment-card__icon"
              aria-hidden="true"
            >
              <feather-icon
                :icon="field.icon"
                size="16"
              />
            </span>
            <span
              class="ar-appointment-card__text"
              :class="{
                'ar-tabular': field.kind === 'date',
                'ar-appointment-card__text--empty': !field.value,
              }"
              :title="field.value || null"
            >{{ field.value || EMPTY_VALUE }}</span>
          </template>
        </dd>
      </div>
    </dl>

    <div
      v-if="hasMeetingRow"
      class="ar-appointment-card__meeting"
    >
      <span class="ar-appointment-card__format">
        <feather-icon
          :icon="meeting.online ? 'VideoIcon' : 'MapPinIcon'"
          size="16"
          aria-hidden="true"
        />
        {{ meeting.online ? NEW_COPY.online : NEW_COPY.inPerson }}
      </span>
      <template v-if="meeting.online && meeting.url">
        <span
          class="ar-appointment-card__dot"
          aria-hidden="true"
        />
        <a
          class="ar-link"
          :href="meeting.url"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ NEW_COPY.meetingLink }}
          <feather-icon
            icon="ExternalLinkIcon"
            size="14"
            aria-hidden="true"
          />
        </a>
      </template>
      <template v-else-if="!meeting.online && meeting.address">
        <span
          class="ar-appointment-card__dot"
          aria-hidden="true"
        />
        <span
          class="ar-appointment-card__address"
          :title="meeting.address"
        >{{ meeting.address }}</span>
      </template>
      <slot name="meeting-actions" />
    </div>
  </section>
</template>

<script>
import {
  A11Y_COPY,
  APPOINTMENT_FIELD_LABELS,
  APP_COPY,
  NEW_COPY,
} from '@/constants/appointment_report_v2'
import { X_MOMENT } from '@/constants/appointment_types'
import { appointmentEndForDisplay, formatDateTime, initialsOf } from '../helpers/report_ui'

const EMPTY_VALUE = '—'
const IN_PERSON_FORMAT = 'in_pearson' // grafia do backend (`meeting_format_types`)

const isHttpUrl = value => {
  if (typeof value !== 'string' || !value.trim()) return false
  try {
    const url = new URL(value.trim())
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch (error) {
    return false
  }
}

/**
 * Cabeçalho do Report (Figma, 6 campos em grade 3×2): Cliente · PN Responsável · PN Especialista ·
 * Tipo de Compromisso · Data de Início · Data de Término, + a linha do formato (Online com "Abrir link da
 * reunião" / Presencial com o endereço).
 *
 * `appointment` = o objeto `appointment` de GET /api/appointments/{id}/report/draft
 * (`{ customer: {name}, consultant: {name}, consultant_specialist: {name} | null, event_type,
 * event_type_description, appointment_start, appointment_end, meeting_format, meeting_room_link }`).
 */
export default {
  name: 'ReportAppointmentCard',
  props: {
    appointment: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showMeetingRow: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      A11Y_COPY,
      NEW_COPY,
      EMPTY_VALUE,
    }
  },
  computed: {
    source() {
      return this.appointment || {}
    },
    eventTypeLabel() {
      const { event_type_description: description, event_type: eventType } = this.source
      if (description) return description
      return Number(eventType) === X_MOMENT ? APP_COPY.momentX : ''
    },
    fields() {
      const person = (key, entity) => {
        const name = entity && entity.name ? String(entity.name) : ''
        return {
          key,
          kind: 'person',
          label: APPOINTMENT_FIELD_LABELS[key],
          value: name,
          initials: initialsOf(name),
        }
      }
      const date = (key, value) => ({
        key,
        kind: 'date',
        label: APPOINTMENT_FIELD_LABELS[key],
        value: formatDateTime(value),
        icon: 'CalendarIcon',
      })

      return [
        person('customer', this.source.customer),
        person('consultant', this.source.consultant),
        person('specialist', this.source.consultant_specialist),
        {
          key: 'eventType',
          kind: 'type',
          label: APPOINTMENT_FIELD_LABELS.eventType,
          value: this.eventTypeLabel,
          icon: 'FileTextIcon',
        },
        date('start', this.source.appointment_start),
        date('end', appointmentEndForDisplay(this.source.appointment_end)),
      ]
    },
    meeting() {
      if (this.loading || !this.appointment) return null
      const { meeting_format: format, meeting_room_link: link } = this.source
      if (!format && !link) return null
      const online = format !== IN_PERSON_FORMAT
      const text = typeof link === 'string' ? link.trim() : ''

      return {
        online,
        url: online && isHttpUrl(text) ? text : '',
        address: online ? '' : text,
      }
    },
    hasMeetingRow() {
      return this.showMeetingRow && !!this.meeting
    },
  },
}
</script>

<style lang="scss" src="@/assets/scss/_appointment-report.scss"></style>

<style lang="scss" scoped>
.ar-appointment-card {
  padding: 20px 24px 0;
}

.ar-appointment-card__grid {
  margin: 0;
  padding-bottom: 20px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 24px;
  row-gap: 16px;
}

// Com a linha do formato, 16px até a divisória (board v4-report).
.ar-appointment-card--with-meeting .ar-appointment-card__grid {
  padding-bottom: 16px;
}

.ar-appointment-card__field {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ar-appointment-card__label {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  color: var(--ar-text-label);
}

.ar-appointment-card__value {
  min-width: 0;
  min-height: 28px;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-1);
}

.ar-appointment-card__icon {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--ar-radius-control);
  background: var(--ar-bg-field);
  color: var(--ar-text-2);
}

.ar-appointment-card__text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: pre;
}

.ar-appointment-card__text--empty {
  color: var(--ar-text-muted);
}

.ar-appointment-card__skeleton {
  width: 64%;
  height: 14px;
  border-radius: var(--ar-radius-thumb);
  background: var(--ar-bg-raised);
}

.ar-appointment-card__meeting {
  min-height: 48px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  padding: 12px 0;
  border-top: 1px solid var(--ar-divider);
}

.ar-appointment-card__format {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-2);
}

.ar-appointment-card__dot {
  width: 4px;
  height: 4px;
  border-radius: var(--ar-radius-pill);
  background: var(--ar-control);
}

.ar-appointment-card__address {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-1);
}

@media (max-width: 991.98px) {
  .ar-appointment-card__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 575.98px) {
  .ar-appointment-card {
    padding: 16px 16px 0;
  }

  .ar-appointment-card__grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
